import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Scissors, Calendar, Clock, User, Phone, Mail, Globe, CheckCircle, Loader2, ArrowLeft, Users, CreditCard, AlertCircle, Star, MapPin, X } from 'lucide-react';
import SkeletonLoading, { SkeletonHero, SkeletonServiceCard, SkeletonBarberCard, SkeletonTimeSlot, SkeletonReviewCard, SkeletonQuickInfoCard } from '../components/SkeletonLoading';
import { getBarbershop, getBarbershopServices, getAvailableSlots, createAppointment } from '../utils/api';
import api from '../utils/api';
import { formatDateDisplay, formatDate, cn } from '../utils/helpers';

export default function BookingPage() {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [barbershop, setBarbershop] = useState(null);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stories, setStories] = useState([]);
  const [customerPlans, setCustomerPlans] = useState([]);
  const [activeStory, setActiveStory] = useState(null);
  const [avgRating, setAvgRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [reviewsEnabled, setReviewsEnabled] = useState(true);
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5, 6]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ customer_name: '', rating: 5, comment: '' });
  
  const [selectedService, setSelectedService] = useState(null);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedOrderBumps, setSelectedOrderBumps] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    observacoes: '',
    acceptedTerms: false
  });

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [paymentPreference, setPaymentPreference] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [subscribePlan, setSubscribePlan] = useState(null);
  const [subscribeForm, setSubscribeForm] = useState({ nome: '', telefone: '', email: '' });
  const [membershipPayment, setMembershipPayment] = useState(null);
  const [showCustomerPlans, setShowCustomerPlans] = useState(false);

  // Refs to track current selections for polling
  const selectedServiceRef = useRef(null);
  const selectedBarberRef = useRef(null);
  const selectedDateRef = useRef('');

  // Determine barbershop identifier (slug takes precedence)
  const shopIdentifier = slug || id;
  const getDocId = (doc) => doc?.id || doc?._id;
  const apiBaseUrl = import.meta.env.VITE_API_URL || `${window.location.protocol}//${window.location.hostname}:3001`;
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) {
      try {
        const parsed = new URL(url);
        if (['localhost', '127.0.0.1', '0.0.0.0'].includes(parsed.hostname)) {
          return `${apiBaseUrl}${parsed.pathname}`;
        }
      } catch {
        return url;
      }
      return url;
    }
    return `${apiBaseUrl}${url}`;
  };

  const calculateDepositAmount = (servicePrice, depositType, depositValue) => {
    const price = Number(servicePrice || 0);
    const value = Number(depositValue || 0);
    if (!price || !value) return 0;
    if (depositType === 'percentage') return Math.min(price, Number(((price * value) / 100).toFixed(2)));
    return Math.min(price, Number(value.toFixed(2)));
  };


  useEffect(() => {
    loadBarbershop();
  }, [shopIdentifier]);

  // Polling automático para verificar status do pagamento
  useEffect(() => {
    let interval;
    if (success && paymentPreference && appointmentId && !polling) {
      setPolling(true);

      // Verificar imediatamente
      checkPaymentStatus(appointmentId);

      // Verificar a cada 5 segundos
      interval = setInterval(() => checkPaymentStatus(appointmentId), 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
      setPolling(false);
    };
  }, [success, paymentPreference, appointmentId, navigate]);

  // Polling automático para atualizar dados da barbearia em tempo real
  useEffect(() => {
    const interval = setInterval(() => {
      // Não atualizar se estiver na tela de sucesso ou formulário de pagamento
      if (!success && step < 6) {
        loadBarbershop(false); // Não mostrar loading durante polling
      }
    }, 30000); // Atualiza a cada 30 segundos

    return () => clearInterval(interval);
  }, [shopIdentifier, success, step]);

  const loadBarbershop = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      let barbershopId = shopIdentifier;
      let barbershopData;

      // If slug is provided, first fetch the barbershop data to get the numeric ID
      if (slug && !id) {
        const slugResponse = await api.get(`/b/${slug}`);
        barbershopData = slugResponse.data;
        barbershopId = getDocId(barbershopData);
      } else {
        // Direct ID fetch
        const shopRes = await getBarbershop(shopIdentifier);
        barbershopData = shopRes.data;
        barbershopId = getDocId(barbershopData);
      }

      if (!barbershopId) {
        throw new Error('ID da barbearia nao retornado pela API');
      }

      const enabledReviews = barbershopData.reviews_enabled !== false;
      setReviewsEnabled(enabledReviews);
      setWorkingDays(
        Array.isArray(barbershopData.dias_funcionamento) && barbershopData.dias_funcionamento.length
          ? barbershopData.dias_funcionamento
          : [1, 2, 3, 4, 5, 6]
      );

      const [servicesRes, barbersRes] = await Promise.all([
        api.get(`/barbershops/${barbershopId}/services`),
        api.get(`/barbershops/${barbershopId}/barbers/active`)
      ]);

      const newServices = servicesRes.data.services || servicesRes.data || [];
      const newBarbers = Array.isArray(barbersRes.data.barbers) ? barbersRes.data.barbers : [];

      // Preserve selections - validate selected IDs against new data using refs
      const prevServiceId = selectedServiceRef.current?.id;
      const prevBarberId = selectedBarberRef.current?.id;
      const prevSelectedDate = selectedDateRef.current;

      // Adicionar backup do localStorage se não houver logo no servidor
      if (!barbershopData.logo_url) {
        const savedLogo = localStorage.getItem(`barbershop_${barbershopId}_logo`);
        if (savedLogo) {
          barbershopData.logo_url = savedLogo;
        }
      }

      setBarbershop(barbershopData);
      setServices(newServices);
      setBarbers(newBarbers);

      try {
        const plansRes = await api.get(`/barbershops/${barbershopId}/customer-plans`);
        setCustomerPlans(plansRes.data.plans || []);
      } catch {
        setCustomerPlans([]);
      }

      try {
        const storiesRes = await api.get(`/barbershops/${barbershopId}/gallery/stories`);
        setStories(storiesRes.data.stories || []);
      } catch {
        setStories([]);
      }

      // Carregar reviews
      if (enabledReviews) {
      try {
        const reviewsRes = await api.get(`/barbershops/${barbershopId}/reviews`);
        if (reviewsRes.data.success) {
          setReviews(reviewsRes.data.reviews || []);
          setAvgRating(reviewsRes.data.avgRating || 0);
          setTotalReviews(reviewsRes.data.totalReviews || 0);
        }
      } catch (reviewError) {
        setReviews([]);
        setAvgRating(0);
        setTotalReviews(0);
      }
      } else {
        setReviews([]);
        setAvgRating(0);
        setTotalReviews(0);
      }

      // Restore valid selections if they still exist in new data
      if (prevServiceId) {
        const serviceExists = newServices.find(s => s.id === prevServiceId);
        if (!serviceExists) {
          setSelectedService(null);
          selectedServiceRef.current = null;
        }
      }

      if (prevBarberId) {
        const barberExists = newBarbers.find(b => b.id === prevBarberId);
        if (!barberExists) {
          setSelectedBarber(null);
          selectedBarberRef.current = null;
        }
      }

      if (prevSelectedDate) {
        setSelectedDate(prevSelectedDate);
      }

      if (showLoading) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Erro ao carregar barbearia:', err);
      setError('Barbearia não encontrada');
      if (showLoading) {
        setLoading(false);
      }
    }
   };

  const loadAvailableSlots = async (date, serviceId, barberId = null) => {
    if (!date) return;
    
    try {
      setLoading(true);
      const params = {
        data: date,
        servico_id: serviceId
      };
      
      if (barberId) {
        params.barber_id = barberId;
      }
      
      const response = await api.get(`/barbearia/${barbershop.id}/horarios`, { params });
      setAvailableSlots(response.data.fechado ? [] : (response.data.horarios || []));
    } catch (err) {
      console.error('Erro ao carregar horários:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    selectedServiceRef.current = service;
    setSelectedOrderBumps([]);
    setStep(2);
  };

  const orderBumpServices = services.filter(service =>
    service.order_bump_enabled && service.id !== selectedService?.id
  );

  const totalPrice = (selectedService?.preco || 0) + selectedOrderBumps.reduce((sum, service) => sum + (service.preco || 0), 0);
  const estimatedDepositAmount = barbershop?.deposit_enabled
    ? calculateDepositAmount(totalPrice, barbershop.deposit_type, barbershop.deposit_value)
    : 0;

  const toggleOrderBump = (service) => {
    setSelectedOrderBumps((current) => (
      current.some(item => item.id === service.id)
        ? current.filter(item => item.id !== service.id)
        : [...current, service]
    ));
  };

  const handleBarberSelect = (barber) => {
    setSelectedBarber(barber);
    selectedBarberRef.current = barber;
    setStep(3);
  };

  const handleDateSelect = (date) => {
    // Criar data local corretamente para evitar problema de timezone
    const [year, month, day] = date.split('-');
    const localDate = new Date(year, month - 1, day);
    setSelectedDate(date);
    selectedDateRef.current = date;
    loadAvailableSlots(date, selectedService?.id, selectedBarber?.id);
    setStep(4);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(5);
  };

  const handlePaymentSelect = (method) => {
    setSelectedMembership(null);
    setSelectedPaymentMethod(method);
    setStep(6);
  };

  const handleMembershipPaymentSelect = async () => {
    setError('');
    if (!formData.telefone || !selectedService?.id) {
      setError('Informe seu telefone e selecione um servico para usar seu plano.');
      return;
    }

    try {
      const response = await api.get(`/barbershops/${barbershop.id}/customer-plans/active-membership`, {
        params: { telefone: formData.telefone, service_id: selectedService.id }
      });

      if (!response.data.active || !response.data.can_use) {
        setError('Nao encontramos um plano ativo para este servico neste telefone.');
        return;
      }

      setSelectedMembership(response.data.membership);
      setSelectedPaymentMethod('membership');
      setStep(6);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao validar plano ativo');
    }
  };

  const subscribeToPlan = async (e) => {
    e.preventDefault();
    if (!subscribePlan) return;
    setError('');
    setLoading(true);
    try {
      const response = await api.post(`/barbershops/${barbershop.id}/customer-plans/${subscribePlan.id}/subscribe`, subscribeForm);
      setMembershipPayment({
        plan: subscribePlan,
        membership: response.data.membership,
        pix: response.data.pix
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar assinatura');
    } finally {
      setLoading(false);
    }
  };

  const checkMembershipPayment = async () => {
    if (!membershipPayment?.membership?.id) return;
    setLoading(true);
    try {
      const response = await api.get(`/barbershops/${barbershop.id}/customer-plans/memberships/${membershipPayment.membership.id}/status`);
      if (response.data.active) {
        setMembershipPayment(prev => ({ ...prev, membership: { ...prev.membership, status: 'active', payment_status: 'pago' } }));
        setSubscribePlan(null);
      } else if (response.data.expired) {
        setError('O Pix do plano expirou. Gere uma nova assinatura.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao verificar pagamento do plano');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    // Frontend validation (e.preventDefault bypasses HTML5 required)
    if (!reviewForm.customer_name?.trim()) {
      alert('Por favor, digite seu nome');
      return;
    }
    if (!reviewForm.rating || reviewForm.rating < 1 || reviewForm.rating > 5) {
      alert('Por favor, selecione uma avaliação válida (1-5 estrelas)');
      return;
    }

    try {
      await api.post(`/barbershops/${barbershop.id}/reviews`, {
        customer_name: reviewForm.customer_name.trim(),
        rating: reviewForm.rating,
        comment: reviewForm.comment?.trim() || null
      });
      // Recarregar reviews
      const reviewsRes = await api.get(`/barbershops/${barbershop.id}/reviews`);
      if (reviewsRes.data.success) {
        setReviews(reviewsRes.data.reviews || []);
        setAvgRating(reviewsRes.data.avgRating || 0);
        setTotalReviews(reviewsRes.data.totalReviews || 0);
      }
      setShowReviewModal(false);
      setReviewForm({ customer_name: '', rating: 5, comment: '' });
    } catch (error) {
      console.error('Erro ao enviar avaliação:', error);
      if (error.response?.data?.error) {
        alert(`Erro: ${error.response.data.error}`);
      } else {
        alert('Erro ao enviar avaliação. Tente novamente.');
      }
    }
  };

  const checkPaymentStatus = async (appointmentId) => {
    try {
      const response = await api.post(`/payments/check-appointment/${appointmentId}`);
      if (response.data?.payment_status === 'pago') {
        setPolling(false);
      }
      if (response.data?.payment_status === 'expired') {
        setPolling(false);
        setError('O Pix expirou. Faça um novo agendamento para reservar este horário.');
      }
      return response.data;
    } catch (err) {
      console.error('Erro ao verificar status do pagamento:', err);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const appointmentData = {
        barbershopId: barbershop.id,
        barber_id: selectedBarber?.id,
        nome: formData.nome,
        telefone: formData.telefone,
        servico_id: selectedService?.id,
        data: selectedDate,
        horario: selectedTime,
        payment_method: selectedPaymentMethod,
        customer_membership_id: selectedMembership?.id,
        acceptedTerms: formData.acceptedTerms,
        acceptedPrivacyPolicy: formData.acceptedTerms,
        whatsappConsent: true,
        observacoes: [
          formData.observacoes,
          selectedOrderBumps.length ? `Order bumps: ${selectedOrderBumps.map(item => item.nome).join(', ')}` : ''
        ].filter(Boolean).join('\n')
      };


      const shouldCreatePix = selectedPaymentMethod === 'online'
        || (selectedPaymentMethod === 'presencial' && barbershop?.deposit_enabled);

      // Se for pagamento online ou sinal, criar agendamento e PIX juntos
      if (shouldCreatePix) {
        const appointmentResponse = await createAppointment(appointmentData);
        
        if (!appointmentResponse.data || !appointmentResponse.data.appointment) {
          console.error('❌ Resposta inválida:', appointmentResponse);
          throw new Error('Resposta inválida do servidor');
        }
        
        setAppointmentId(appointmentResponse.data.appointment.id);
        
        // Criar código PIX
        const paymentResponse = await api.post('/payments/create-pix', {
          appointment_id: appointmentResponse.data.appointment.id,
          service_name: selectedService.nome,
          service_price: appointmentResponse.data.appointment.deposit_required ? appointmentResponse.data.appointment.deposit_amount : totalPrice,
          client_email: formData.email,
          payment_purpose: appointmentResponse.data.appointment.deposit_required ? 'deposit' : 'full'
        });
        
        setPaymentPreference(paymentResponse.data);
        setSuccess(true); // Mostra tela de sucesso com PIX
      } else {
        // Pagamento presencial, criar agendamento normalmente
        await createAppointment(appointmentData);
        setSuccess(true);
      }
      
      // Atualizar horários disponíveis após criar agendamento
      if (selectedDate && selectedService?.id) {
        loadAvailableSlots(selectedDate, selectedService.id, selectedBarber?.id);
      }
    } catch (err) {
      console.error('❌ Erro completo:', err);
      console.error('❌ Erro response:', err.response);
      console.error('❌ Erro response data:', err.response?.data);
      
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao criar agendamento';
      const errorCode = err.response?.data?.code;
      
      if (errorCode === 'SLOT_OCCUPIED') {
        setError(`${errorMessage}. ${err.response?.data?.suggestion || ''}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Gerar datas dos próximos 14 dias
  const generateDates = () => {
    const dates = [];
    // Forçar uso da data local atual
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    
    let offset = 0;
    while (dates.length < 14 && offset < 45) {
      // Criar nova instância de Date para cada dia para evitar bug de modificação
      const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
      offset += 1;

      if (!workingDays.includes(date.getDay())) {
        continue;
      }
      // Formatar data local manualmente para evitar problemas de timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      dates.push(dateStr);
      const i = dates.length - 1;
      
      // Debug para verificar as datas geradas
      if (dates.length <= 3) {
      }
    }
    
    return dates;
  };

  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (loading && !barbershop) {
    return (
      <div className="min-h-screen bg-dark-900">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            <SkeletonHero />
            <div className="mt-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <SkeletonQuickInfoCard key={i} />
                ))}
              </div>
            </div>
            <div className="mt-12">
              <div className="h-8 bg-dark-700 rounded w-48 mb-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <SkeletonServiceCard key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !barbershop) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4">
        <div className="card p-8 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <Scissors className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">{error}</h2>
          <p className="text-gray-400">Verifique o link ou entre em contato com a barbearia.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4 py-8">
        <div className="card p-8 text-center max-w-md animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {paymentPreference ? 'Agendamento Criado!' : 'Agendamento Confirmado!'}
          </h2>
          <p className="text-gray-400 mb-6">
            {paymentPreference 
              ? 'Para reservar seu horário, pague o sinal via PIX abaixo. A confirmação acontece após a aprovação.'
              : 'Seu agendamento foi realizado com sucesso. Aguarde a confirmação da barbearia.'}
          </p>
          <div className="bg-dark-700 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-center gap-3 mb-3">
              {barbershop?.logo_url && (
                <img 
                  src={`${import.meta.env.VITE_API_URL || 'http://192.168.0.110:3001'}${barbershop.logo_url}`}
                  alt="Logo da barbearia" 
                  className="w-12 h-12 rounded-lg object-cover"
                />
              )}
              <div>
                <p className="text-gray-400 text-sm mb-1">Barbearia</p>
                <p className="text-white font-medium">{barbershop?.nome}</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Data e Horário</p>
            <p className="text-white font-medium mb-3">
              {formatDateDisplay(selectedDate)} às {selectedTime}
            </p>
            <p className="text-gray-400 text-sm mb-1">Serviço</p>
                <p className="text-white font-medium mb-3">
                  {[selectedService?.nome, ...selectedOrderBumps.map(item => item.nome)].filter(Boolean).join(' + ')}
                </p>
            {paymentPreference && (
              <>
                <p className="text-gray-400 text-sm mb-1">
                  {paymentPreference?.amount < totalPrice ? 'Sinal' : 'Valor pago online'}
                </p>
                <p className="text-white font-medium mb-3">R$ {Number(paymentPreference?.amount || totalPrice).toFixed(2)}</p>
              </>
            )}
          </div>

          {/* Mostrar QR Code PIX se for pagamento online */}
          {paymentPreference && (
            <div className="bg-dark-700 rounded-xl p-4 mb-6">
              <h3 className="text-lg font-semibold text-white mb-2">
                {paymentPreference.amount < totalPrice ? 'Pagar sinal via PIX' : 'Pagar online via PIX'}
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                {paymentPreference.amount < totalPrice
                  ? `Para reservar seu horário, pague um sinal de R$ ${Number(paymentPreference.amount || 0).toFixed(2)}. O restante fica R$ ${(totalPrice - Number(paymentPreference.amount || 0)).toFixed(2)}.`
                  : `Pague R$ ${Number(paymentPreference.amount || 0).toFixed(2)} para confirmar o agendamento online.`}
              </p>
              
              {/* QR Code */}
              {paymentPreference.qr_code_base64 && (
                <div className="bg-white p-4 rounded-xl mb-4">
                  <img 
                    src={`data:image/png;base64,${paymentPreference.qr_code_base64}`} 
                    alt="QR Code PIX"
                    className="w-48 h-48 mx-auto"
                  />
                </div>
              )}

              {/* Código PIX Copiar e Colar */}
              {paymentPreference.qr_code && (
                <div className="mb-4">
                  <label className="label text-sm text-center">Código PIX (Copiar e Colar)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={paymentPreference.qr_code}
                      className="input flex-1 text-xs"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(paymentPreference.qr_code);
                        alert('Código PIX copiado!');
                      }}
                      className="btn-primary px-4"
                    >
                      Copiar
                    </button>
                  </div>
                </div>
              )}

              {paymentPreference.ticket_url && (
                <a
                  href={paymentPreference.ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:underline text-center block mb-4"
                >
                  Abrir código PIX no Mercado Pago
                </a>
              )}

              <button
                onClick={async () => {
                  if (appointmentId) {
                    const status = await checkPaymentStatus(appointmentId);
                    if (status && status.payment_status === 'pago') {
                      alert('Pagamento confirmado! Agendamento confirmado.');
                    } else {
                      alert('Pagamento ainda não confirmado. Aguarde um momento.');
                    }
                  }
                }}
                className="btn-secondary w-full mb-2"
              >
                Já paguei
              </button>
              <p className="text-xs text-gray-500">
                O sistema verificará automaticamente a cada 5 segundos. O Pix expira em 15 minutos.
              </p>
            </div>
          )}

          <button 
            onClick={() => window.location.reload()}
            className="btn-primary w-full"
          >
            Novo Agendamento
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080912]">
      {/* Header leve */}
      <div className="relative overflow-hidden border-b border-white/10 bg-[#0b0c16]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(124,58,237,0.18),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.035),transparent)]" />
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: 'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
          backgroundSize: '44px 44px'
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-7 sm:py-9">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-4">
              <div 
                className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-dark-800 overflow-hidden border border-white/10"
              >
                {barbershop?.logo_url ? (
                  <img
                    src={`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${barbershop.logo_url}`}
                    alt={barbershop.nome}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="w-12 h-12 text-white mx-auto my-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="3"></circle><circle cx="6" cy="18" r="3"></circle><line x1="20" y1="4" x2="8.12" y2="15.88"></line><line x1="14.47" y1="14.48" x2="20" y2="20"></line><line x1="8.12" y1="8.12" x2="12" y2="12"></line></svg>';
                    }}
                  />
                ) : (
                  <Scissors className="w-12 h-12 text-white mx-auto my-6" />
                )}
              </div>
            </div>

            <div className="inline-flex items-center rounded-full border border-primary-300/15 bg-primary-400/[0.06] px-3 py-1 text-[11px] font-semibold text-primary-200 mb-3">Agendamento online</div>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-2 tracking-tight">{barbershop?.nome}</h1>
            
            {/* Avaliação */}
            {reviewsEnabled && (
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                ))}
              </div>
              <span className="text-white font-semibold">{avgRating.toFixed(1)}</span>
              <span className="text-gray-400">• {totalReviews} avaliações</span>
            </div>
            )}

            {/* Endereço e contato */}
            <div className="flex flex-col items-center gap-3 text-gray-400 text-sm mb-5">
              <div className="flex flex-wrap items-center justify-center gap-4">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {barbershop?.endereco || 'Centro'}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  Aberto agora
                </span>
              </div>

              {(barbershop?.telefone || barbershop?.email || barbershop?.website) && (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {barbershop.telefone && (
                    <a
                      href={`tel:${barbershop.telefone.replace(/\D/g, '')}`}
                      className="inline-flex items-center gap-1 hover:text-white transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      {barbershop.telefone}
                    </a>
                  )}
                  {barbershop.email && (
                    <a
                      href={`mailto:${barbershop.email}`}
                      className="inline-flex items-center gap-1 hover:text-white transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      {barbershop.email}
                    </a>
                  )}
                  {barbershop.website && (
                    <a
                      href={barbershop.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 hover:text-white transition-colors"
                    >
                      <Globe className="w-4 h-4" />
                      Site
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* CTA */}
            <p className="text-base text-gray-300">Escolha o servico e reserve em poucos segundos.</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-5 sm:pt-7">
        <div className="max-w-5xl mx-auto">

        {/* Progress Steps */}
        <div className="sticky top-3 z-20 mb-6 flex justify-center">
        <div className="flex items-center justify-center gap-1.5 overflow-x-auto rounded-full border border-white/10 bg-[#0d0f1a]/90 px-2 py-2 backdrop-blur-xl">
          {[
            { step: 1, label: 'Serviço' },
            { step: 2, label: 'Barbeiro' },
            { step: 3, label: 'Data' },
            { step: 4, label: 'Horário' },
            { step: 5, label: 'Pagamento' }
          ].map((item) => (
            <div key={item.step} className="flex items-center gap-2">
              <div className={cn(
                "h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all px-3 whitespace-nowrap",
                step >= item.step ? "bg-white text-dark-900" : "bg-white/[0.04] text-gray-500"
              )}>
                {item.label}
              </div>
              {item.step < 5 && (
                <div className={cn(
                  "w-4 h-0.5 transition-colors",
                  step > item.step ? "bg-white/70" : "bg-dark-700"
                )} />
              )}
            </div>
          ))}
        </div>
        </div>

        {step === 1 && stories.length > 0 && (
          <div className="mb-8 animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400">Stories/Publicações</h3>
              <span className="text-xs text-gray-500">24h</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {stories.map((story) => (
                <button
                  key={story.id}
                  type="button"
                  onClick={() => setActiveStory(story)}
                  className="w-16 flex-shrink-0 text-center group"
                  title={story.title || 'Publicação'}
                >
                  <span className="block w-12 h-12 mx-auto rounded-full p-[2px] bg-gradient-to-tr from-yellow-400 via-pink-500 to-primary-500 opacity-90 group-hover:opacity-100 transition-opacity">
                    <span className="block w-full h-full rounded-full bg-dark-900 p-[2px]">
                      <img
                        src={getImageUrl(story.image_url)}
                        alt={story.title || 'Publicação'}
                        className="w-full h-full rounded-full object-cover"
                      />
                    </span>
                  </span>
                  <span className="block text-[11px] text-gray-500 mt-1 truncate group-hover:text-gray-300">
                    {story.title || 'Story'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Service Selection */}
        {step === 1 && (
          <div className="mb-8 animate-fade-in">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.065] to-white/[0.025] p-4 sm:p-5 mb-4 shadow-[0_18px_60px_rgba(0,0,0,0.22)]">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-300/50 to-transparent" />
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary-300 mb-2">Comece aqui</p>
                  <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Escolha o servico</h2>
                  <p className="text-sm text-gray-400 mt-1 max-w-xl">Depois voce escolhe barbeiro, data, horario e pagamento.</p>
                </div>
                <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-medium text-gray-300">
                  <Scissors className="w-4 h-4 mr-2" />
                  {services.length} opcoes
                </div>
              </div>
            </div>
            {loading && services.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <SkeletonServiceCard key={i} />
                ))}
              </div>
            ) : services.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-dark-700 bg-white/[0.025] p-8 text-center">
                <Scissors className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">Nenhum servico disponivel</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {services.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => handleServiceSelect(service)}
                    className={cn(
                      "group relative cursor-pointer overflow-hidden rounded-2xl border bg-gradient-to-br from-white/[0.055] to-white/[0.025] p-3.5 sm:p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300/35 hover:bg-white/[0.065] hover:shadow-[0_18px_45px_rgba(0,0,0,0.25)]",
                      selectedService?.id === service.id
                        ? 'border-primary-400/70 bg-primary-500/10 shadow-[0_18px_45px_rgba(124,58,237,0.12)]'
                        : 'border-white/10'
                    )}
                  >
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div
                          className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden ring-1 ring-white/10 transition-transform duration-200 group-hover:scale-[1.03]"
                          style={{ backgroundColor: service.cor + '20', color: service.cor }}
                        >
                          {service.image_url ? (
                            <img src={getImageUrl(service.image_url)} alt={service.nome} className="w-full h-full object-cover" />
                          ) : (
                            <Scissors className="w-7 h-7" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white leading-tight text-base truncate">{service.nome}</h3>
                          <p className="text-xs text-gray-500 mt-1">{service.duracao_minutos} min de atendimento</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg sm:text-xl font-black text-white">R$ {service.preco}</p>
                          <span className={cn(
                            "mt-1 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors",
                            selectedService?.id === service.id
                              ? "bg-primary-500 text-white"
                              : "bg-white/[0.06] text-gray-300 group-hover:bg-primary-500/15 group-hover:text-primary-100"
                          )}>
                            {selectedService?.id === service.id ? 'Escolhido' : 'Escolher'}
                          </span>
                        </div>
                      </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      {step === 1 && customerPlans.length > 0 && (
        <div className="mb-8 animate-fade-in">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.018] p-4">
            <button
              type="button"
              onClick={() => setShowCustomerPlans(prev => !prev)}
              className="flex w-full flex-col sm:flex-row sm:items-center justify-between gap-2 text-left"
            >
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-300">Opcional</p>
                <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">Planos mensais da barbearia</h2>
                <p className="text-xs text-gray-500 mt-1">Para clientes frequentes. O agendamento normal continua acima.</p>
              </div>
              <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs font-semibold text-gray-300">
                {showCustomerPlans ? 'Ocultar planos' : 'Ver planos'}
              </span>
            </button>
            {showCustomerPlans && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
              {customerPlans.map((plan, index) => {
                const badges = ['Mais vendido', 'Melhor custo beneficio', 'Plano VIP'];
                return (
                  <div key={plan.id} className="min-w-[240px] flex-1 rounded-2xl border border-white/10 bg-dark-900/70 p-3.5 transition-all hover:-translate-y-0.5 hover:border-primary-300/25">
                    <div className="flex items-start justify-between gap-3">
                      <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-semibold text-gray-300">
                        {badges[index % badges.length]}
                      </span>
                      <span className="text-[11px] text-gray-500">{plan.duration_days} dias</span>
                    </div>
                    <h3 className="mt-3 font-semibold text-white text-base truncate">{plan.name}</h3>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2 min-h-[32px]">{plan.description || 'Plano mensal para clientes frequentes.'}</p>
                    <div className="mt-3 flex items-end justify-between gap-3">
                      <div>
                        <p className="text-[11px] text-gray-500">Mensalidade</p>
                        <p className="text-2xl font-black text-white">R$ {Number(plan.price || 0).toFixed(2)}</p>
                      </div>
                      <p className="text-xs text-gray-500 text-right">{plan.max_uses_per_month ? `${plan.max_uses_per_month} usos` : 'uso ilimitado'}</p>
                    </div>
                    <button
                      onClick={() => {
                        setSubscribePlan(plan);
                        setMembershipPayment(null);
                        setSubscribeForm({ nome: formData.nome, telefone: formData.telefone, email: formData.email });
                      }}
                      className="mt-3 w-full rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-dark-900 transition-opacity hover:opacity-90"
                    >
                      Assinar agora
                    </button>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </div>
      )}
      {/* Reviews Section - Show only on step 1 */}
      {step === 1 && reviewsEnabled && (
        <div className="mb-10 animate-fade-in">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.018] p-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl border border-yellow-400/15 bg-yellow-400/[0.06] px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <Star className="w-4 h-4 fill-current text-yellow-400" />
                      <span className="text-lg font-black text-white">{avgRating.toFixed(1)}</span>
                    </div>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white tracking-tight">Avaliacoes</h2>
                    <p className="text-xs text-gray-500">{totalReviews} clientes avaliaram</p>
                  </div>
                </div>
                <button onClick={() => setShowReviewModal(true)} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-bold text-gray-200 hover:bg-white/[0.08] transition-colors">
                  Avaliar
                </button>
              </div>

                {reviews.length > 0 ? (
                  <div className="flex gap-2.5 overflow-x-auto pb-1">
                    {reviews.slice(0, 10).map((review) => {
                      const reviewerName = review.customer_name || review.client_name || 'Cliente';
                      return (
                        <div key={review.id || review._id} className="min-w-[210px] rounded-2xl border border-white/10 bg-dark-900/60 p-3 transition-colors hover:border-yellow-300/20">
                          <div className="flex items-start gap-2.5 mb-2">
                            <div className="w-8 h-8 rounded-full bg-white/[0.06] flex items-center justify-center flex-shrink-0 border border-white/10">
                              <span className="text-white font-bold text-xs">{reviewerName.charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-white text-sm truncate">{reviewerName}</p>
                              <div className="flex items-center gap-0.5 mt-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star key={i} className={cn('w-3 h-3 fill-current', i < review.rating ? 'text-yellow-400' : 'text-gray-700')} />
                                ))}
                              </div>
                            </div>
                          </div>
                          <p className="text-gray-400 text-xs leading-relaxed line-clamp-2">{review.comment || 'Atendimento excelente e agenda muito facil de usar.'}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-dark-600 bg-dark-900/45 p-5 text-center">
                    <Star className="w-7 h-7 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-400 mb-3">Ainda nao ha avaliacoes</p>
                    <button onClick={() => setShowReviewModal(true)} className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-white">Seja o primeiro a avaliar</button>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
      {/* Step 2: Barber Selection - Premium Cards */}
      {step === 2 && (
        <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Conheça nossos barbeiros</h2>
                <p className="text-gray-400">Escolha o profissional ideal para você</p>
              </div>
              <button onClick={() => setStep(1)} className="btn-ghost text-sm">
                Voltar
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Opção "Qualquer barbeiro disponível" */}
              <button
                onClick={() => handleBarberSelect(null)}
                className={cn(
                  "card p-6 text-left group relative overflow-hidden transition-all duration-300",
                  "hover:shadow-2xl hover:shadow-primary-500/20 hover:-translate-y-1",
                  selectedBarber === null 
                    ? "border-2 border-primary-500 bg-primary-500/10 shadow-xl shadow-primary-500/20" 
                    : "border border-dark-700 hover:border-primary-500/50"
                )}
              >
                <div className="w-full h-32 rounded-xl mb-4 flex items-center justify-center bg-gradient-to-br from-primary-500/20 to-purple-600/20 overflow-hidden">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center transition-transform group-hover:scale-110">
                    <Users className="w-10 h-10 text-white" />
                  </div>
                </div>
                <h3 className="font-bold text-white text-lg mb-2">Qualquer barbeiro</h3>
                <p className="text-gray-400 text-sm mb-4">Primeira disponibilidade automática</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-dark-700">
                  <span className="text-primary-400 text-sm font-semibold">Mais rápido</span>
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                    selectedBarber === null 
                      ? "bg-primary-500 text-white" 
                      : "bg-dark-700 text-gray-400 group-hover:bg-primary-500 group-hover:text-white"
                  )}>
                    <ArrowLeft className="w-5 h-5 rotate-180" />
                  </div>
                </div>
              </button>
              
              {/* Lista de barbeiros */}
              {loading && barbers.length === 0 ? (
                [...Array(3)].map((_, i) => <SkeletonBarberCard key={i} />)
              ) : (
                barbers.map((barber) => (
                <button
                  key={barber.id}
                  onClick={() => handleBarberSelect(barber)}
                  className={cn(
                    "card p-6 text-left group relative overflow-hidden transition-all duration-300",
                    "hover:shadow-2xl hover:shadow-primary-500/20 hover:-translate-y-1",
                    selectedBarber?.id === barber.id 
                      ? "border-2 border-primary-500 bg-primary-500/10 shadow-xl shadow-primary-500/20" 
                      : "border border-dark-700 hover:border-primary-500/50"
                  )}
                >
                  <div className="w-full h-32 rounded-xl mb-4 flex items-center justify-center overflow-hidden bg-dark-700">
                    {barber.photo_url ? (
                      <img 
                        src={getImageUrl(barber.photo_url)} 
                        alt={barber.name}
                        className="w-full h-full object-cover transition-transform group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                        <User className="w-10 h-10 text-white" />
                      </div>
                    )}
                  </div>
                  
                  <h3 className="font-bold text-white text-lg mb-1">{barber.name}</h3>
                  <p className="text-gray-400 text-sm mb-2">{barber.specialty || 'Profissional especializado'}</p>
                  
                  {barber.rating > 0 && (
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className={cn(
                          "w-3 h-3 fill-current",
                          i < Math.floor(barber.rating) ? "text-yellow-400" : "text-gray-600"
                        )} viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      ))}
                      {barber.review_count > 0 && (
                        <span className="text-xs text-gray-400">({barber.review_count})</span>
                      )}
                    </div>
                  )}

                  {barber.bio && (
                    <p className="text-gray-500 text-xs mb-4 line-clamp-2">{barber.bio}</p>
                  )}

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-dark-700">
                    <span className="text-primary-400 text-sm font-semibold">Ver horários</span>
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                      selectedBarber?.id === barber.id 
                        ? "bg-primary-500 text-white" 
                        : "bg-dark-700 text-gray-400 group-hover:bg-primary-500 group-hover:text-white"
                    )}>
                      <ArrowLeft className="w-5 h-5 rotate-180" />
                    </div>
                  </div>
                </button>
              )))}
            </div>
          </div>
        )}

        {/* Step 3: Date Selection - Premium */}
        {step === 3 && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Escolha a data</h2>
                <p className="text-gray-400">Selecione o melhor dia para você</p>
              </div>
              <button onClick={() => setStep(2)} className="btn-ghost text-sm">
                Voltar
              </button>
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {generateDates().map((date) => {
                const [year, month, day] = date.split('-');
                const localDate = new Date(year, month - 1, day);
                const isToday = date === getLocalDateString();
                
                return (
                  <button
                    key={date}
                    onClick={() => handleDateSelect(date)}
                    className={cn(
                      "card p-4 text-center transition-all duration-300 relative overflow-hidden",
                      "hover:shadow-xl hover:shadow-primary-500/20 hover:-translate-y-1",
                      selectedDate === date 
                        ? "border-2 border-primary-500 bg-primary-500/10 shadow-xl shadow-primary-500/20" 
                        : "border border-dark-700 hover:border-primary-500/50"
                    )}
                  >
                    {isToday && (
                      <div className="absolute top-2 right-2">
                        <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                      </div>
                    )}
                    <p className="text-xs text-gray-400 uppercase mb-1 font-medium">
                      {localDate.toLocaleDateString('pt-BR', { weekday: 'short' })}
                    </p>
                    <p className="text-2xl font-bold text-white mb-1">
                      {localDate.getDate()}
                    </p>
                    <p className="text-xs text-gray-400 font-medium">
                      {localDate.toLocaleDateString('pt-BR', { month: 'short' })}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 4: Time Selection - Premium */}
        {step === 4 && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Escolha o horário</h2>
                <p className="text-gray-400">{formatDateDisplay(selectedDate)}</p>
              </div>
              <button onClick={() => setStep(3)} className="btn-ghost text-sm">
                Voltar
              </button>
            </div>
            
            {/* Info Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              <div className="card p-3 text-center border border-primary-500/30 bg-primary-500/5">
                <Clock className="w-5 h-5 text-primary-400 mx-auto mb-1" />
                <p className="text-xs text-gray-400">Tolerância</p>
                <p className="text-sm font-semibold text-white">10 min</p>
              </div>
              <div className="card p-3 text-center border border-green-500/30 bg-green-500/5">
                <CheckCircle className="w-5 h-5 text-green-400 mx-auto mb-1" />
                <p className="text-xs text-gray-400">Agendamento</p>
                <p className="text-sm font-semibold text-white">Online</p>
              </div>
              <div className="card p-3 text-center border border-blue-500/30 bg-blue-500/5">
                <CreditCard className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <p className="text-xs text-gray-400">Pagamento</p>
                <p className="text-sm font-semibold text-white">PIX</p>
              </div>
              <div className="card p-3 text-center border border-purple-500/30 bg-purple-500/5">
                <Users className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                <p className="text-xs text-gray-400">Profissional</p>
                <p className="text-sm font-semibold text-white">Premium</p>
              </div>
            </div>

            {/* Aviso de tolerância */}
            <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-start gap-3">
              <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-400">
                Chegue com até 10 minutos de antecedência. Após 10 minutos de atraso, seu horário pode ser liberado.
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                {availableSlots.map((slot) => {
                  const isAlmoco = slot.barbeiros?.some(b => b.almoco);
                  
                  return (
                    <button
                      key={slot.horario}
                      onClick={() => slot.disponivel && handleTimeSelect(slot.horario)}
                      disabled={!slot.disponivel}
                      className={cn(
                        "card p-4 text-center transition-all duration-300 relative overflow-hidden",
                        "hover:shadow-xl hover:shadow-primary-500/20 hover:-translate-y-1",
                        slot.disponivel 
                          ? selectedTime === slot.horario
                            ? "border-2 border-primary-500 bg-primary-500/10 shadow-xl shadow-primary-500/20"
                            : "border border-dark-700 hover:border-primary-500/50"
                          : isAlmoco
                            ? "bg-yellow-900/10 border border-yellow-700/30 cursor-not-allowed opacity-60"
                            : "bg-dark-800 border border-dark-700 cursor-not-allowed opacity-40"
                      )}
                    >
                      <p className="text-lg font-bold text-white mb-1">{slot.horario}</p>
                      {!slot.disponivel && (
                        <p className="text-xs text-gray-500">
                          {slot.past ? 'Passou' : isAlmoco ? 'Almoço' : 'Ocupado'}
                        </p>
                      )}
                      {selectedTime === slot.horario && (
                        <div className="absolute inset-0 bg-primary-500/5 animate-pulse" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 5: Payment Method Selection - Premium */}
        {step === 5 && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Forma de pagamento</h2>
                <p className="text-gray-400">Escolha como deseja pagar</p>
              </div>
              <button onClick={() => setStep(4)} className="btn-ghost text-sm">
                Voltar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {customerPlans.length > 0 && (
                <button
                  onClick={handleMembershipPaymentSelect}
                  className={cn(
                    "card p-5 text-left group relative overflow-hidden transition-all duration-300",
                    "hover:shadow-xl hover:shadow-primary-500/15 hover:-translate-y-0.5",
                    selectedPaymentMethod === 'membership'
                      ? "border-2 border-primary-500 bg-primary-500/10 shadow-xl shadow-primary-500/20"
                      : "border border-dark-700 hover:border-primary-500/50"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-6 h-6 text-primary-300" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white text-base mb-1">Usar plano ativo</h3>
                      <p className="text-gray-400 text-sm">Validar pelo telefone informado.</p>
                    </div>
                  </div>
                </button>
              )}

              <button
                onClick={() => handlePaymentSelect('presencial')}
                className={cn(
                  "card p-6 text-left group relative overflow-hidden transition-all duration-300",
                  "hover:shadow-2xl hover:shadow-green-500/20 hover:-translate-y-1",
                  selectedPaymentMethod === 'presencial' 
                    ? "border-2 border-green-500 bg-green-500/10 shadow-xl shadow-green-500/20" 
                    : "border border-dark-700 hover:border-green-500/50"
                )}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-600/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-7 h-7 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-white text-lg mb-2">Pagar na barbearia</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      {barbershop?.deposit_enabled
                        ? `Pague R$ ${estimatedDepositAmount.toFixed(2)} de sinal agora e o restante na barbearia`
                        : 'Pague em dinheiro, cartão ou PIX na hora do corte'}
                    </p>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-green-400 font-medium">
                        {barbershop?.deposit_enabled ? `Restante: R$ ${(totalPrice - estimatedDepositAmount).toFixed(2)}` : 'Sem taxas adicionais'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-dark-700">
                  <p className="text-2xl font-bold text-white">
                    R$ {(barbershop?.deposit_enabled ? estimatedDepositAmount : totalPrice).toFixed(2)}
                  </p>
                </div>
              </button>

              <button
                onClick={() => handlePaymentSelect('online')}
                className={cn(
                  "card p-6 text-left group relative overflow-hidden transition-all duration-300",
                  "hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-1",
                  selectedPaymentMethod === 'online' 
                    ? "border-2 border-blue-500 bg-blue-500/10 shadow-xl shadow-blue-500/20" 
                    : "border border-dark-700 hover:border-blue-500/50"
                )}
              >
                {selectedPaymentMethod === 'online' && (
                  <div className="absolute top-3 right-3 px-2 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full">
                    Recomendado
                  </div>
                )}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-600/20 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-7 h-7 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-white text-lg mb-2">Pagar Online</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Pague o valor total agora com PIX via Mercado Pago
                    </p>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      <span className="text-xs text-blue-400 font-medium">Barbeiro sera avisado como pago</span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-dark-700">
                  <p className="text-2xl font-bold text-white">
                    R$ {totalPrice.toFixed(2)}
                  </p>
                </div>
              </button>
            </div>

            {orderBumpServices.length > 0 && (
              <div className="card p-5 mt-6 border border-yellow-500/20 bg-yellow-500/5">
                <h3 className="font-bold text-white mb-3">Adicionar ao atendimento</h3>
                <div className="space-y-3">
                  {orderBumpServices.map((service) => {
                    const checked = selectedOrderBumps.some(item => item.id === service.id);
                    return (
                      <label key={service.id} className="flex items-center justify-between gap-4 rounded-lg border border-dark-700 p-3 cursor-pointer hover:border-yellow-500/40">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOrderBump(service)}
                          />
                          <div>
                            <p className="font-semibold text-white">{service.order_bump_label || service.nome}</p>
                            <p className="text-xs text-gray-400">{service.nome}</p>
                          </div>
                        </div>
                        <span className="font-bold text-yellow-300">R$ {service.preco.toFixed(2)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 6: Personal Info - Premium */}
        {step === 6 && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Seus dados</h2>
                <p className="text-gray-400">Preencha para confirmar o agendamento</p>
              </div>
              <button onClick={() => setStep(5)} className="btn-ghost text-sm">
                Voltar
              </button>
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="label mb-2">Nome completo</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    placeholder="Digite seu nome completo"
                    className="input pl-12"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="label mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  WhatsApp
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    required
                    placeholder="(11) 99999-9999"
                    className="input pl-12"
                    value={formData.telefone}
                    onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                  />
                </div>
              </div>

              {(selectedPaymentMethod === 'online' || (selectedPaymentMethod === 'presencial' && barbershop?.deposit_enabled)) && (
                <div>
                  <label className="label mb-2">E-mail</label>
                  <div className="relative">
                    <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      required
                      placeholder="seu@email.com"
                      className="input pl-12"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Necessário para envio do recibo do Mercado Pago
                  </p>
                </div>
              )}

              <div>
                <label className="label mb-2">Observações (opcional)</label>
                <textarea
                  rows={3}
                  placeholder="Alguma observação especial?"
                  className="input resize-none"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                />
              </div>

              <div className="rounded-xl border border-dark-700 bg-dark-800/60 p-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    checked={formData.acceptedTerms}
                    onChange={(e) => setFormData({ ...formData, acceptedTerms: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-300 leading-relaxed">
                    Li e aceito os{' '}
                    <Link to="/terms" target="_blank" className="text-primary-300 hover:text-primary-200">
                      Termos de Uso
                    </Link>{' '}
                    e a{' '}
                    <Link to="/privacy" target="_blank" className="text-primary-300 hover:text-primary-200">
                      Politica de Privacidade
                    </Link>.
                  </span>
                </label>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Ao continuar, voce concorda em receber mensagens automaticas relacionadas ao seu agendamento pelo WhatsApp.
                </p>
              </div>

              {/* Summary Card */}
              <div className="card p-6 border border-primary-500/30 bg-primary-500/5 space-y-4">
                <h3 className="font-bold text-white mb-4">Resumo do agendamento</h3>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                    <Scissors className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Serviço</p>
                    <p className="text-white font-semibold">{selectedService?.nome}</p>
                    {selectedOrderBumps.length > 0 && (
                      <p className="text-sm text-yellow-300 mt-1">
                        + {selectedOrderBumps.map(item => item.nome).join(', ')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Data e horário</p>
                    <p className="text-white font-semibold">{formatDateDisplay(selectedDate)} às {selectedTime}</p>
                  </div>
                </div>

                {selectedBarber && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Profissional</p>
                      <p className="text-white font-semibold">{selectedBarber.name}</p>
                    </div>
                  </div>
                )}

                <div className="border-t border-dark-700 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-medium">
                      {barbershop?.deposit_enabled && selectedPaymentMethod === 'presencial' ? 'Total do corte' : 'Total a pagar'}
                    </span>
                    <span className="text-3xl font-bold text-primary-400">
                      R$ {totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-4 text-lg font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Confirmando...
                  </>
                ) : (
                  'Confirmar Agendamento'
                )}
              </button>
            </form>
          </div>
        )}
        </div>
      </div>

      {/* Review Modal */}
      {activeStory && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setActiveStory(null)}
        >
          <div
            className="relative w-full max-w-md animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-primary-500 blur opacity-80" />
            <div className="relative overflow-hidden rounded-3xl bg-dark-900 border border-white/10 shadow-2xl">
              <button
                type="button"
                onClick={() => setActiveStory(null)}
                className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <img
                src={getImageUrl(activeStory.image_url)}
                alt={activeStory.title || 'Publicação'}
                className="w-full max-h-[78vh] object-contain bg-black"
              />

              {(activeStory.title || activeStory.description) && (
                <div className="p-5 bg-gradient-to-t from-black to-dark-900">
                  {activeStory.title && (
                    <h3 className="text-xl font-bold text-white">{activeStory.title}</h3>
                  )}
                  {activeStory.description && (
                    <p className="text-gray-300 mt-2">{activeStory.description}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showReviewModal && reviewsEnabled && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="card max-w-md w-full p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Deixe sua avaliação</h3>
              <button 
                onClick={() => setShowReviewModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div>
                <label className="label mb-2">Seu nome</label>
                <input
                  type="text"
                  required
                  placeholder="Digite seu nome"
                  className="input"
                  value={reviewForm.customer_name}
                  onChange={(e) => setReviewForm({...reviewForm, customer_name: e.target.value})}
                />
              </div>

              <div>
                <label className="label mb-2">Avaliação</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewForm({...reviewForm, rating: star})}
                      className="text-3xl transition-transform hover:scale-110"
                    >
                      <Star 
                        className={cn(
                          "w-8 h-8",
                          star <= reviewForm.rating ? "fill-current text-yellow-400" : "fill-current text-gray-600"
                        )}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label mb-2">Comentário (opcional)</label>
                <textarea
                  rows={3}
                  placeholder="Conte sua experiência..."
                  className="input resize-none"
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                />
              </div>

              <button type="submit" className="btn-primary w-full">
                Enviar avaliação
              </button>
            </form>
          </div>
        </div>
      )}

      {subscribePlan && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="card max-w-lg w-full p-6 animate-scale-in max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h3 className="text-xl font-bold text-white">Assinar {subscribePlan.name}</h3>
                <p className="text-gray-400 text-sm mt-1">R$ {Number(subscribePlan.price || 0).toFixed(2)} por {subscribePlan.duration_days} dias.</p>
              </div>
              <button onClick={() => setSubscribePlan(null)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            {membershipPayment ? (
              <div className="space-y-4">
                {membershipPayment.membership?.status === 'active' || membershipPayment.membership?.payment_status === 'pago' ? (
                  <div className="rounded-xl border border-green-500/20 bg-green-500/10 p-4 text-green-300">
                    Plano ativado. Agora voce ja pode agendar usando o telefone informado.
                  </div>
                ) : (
                  <>
                    {membershipPayment.pix?.qr_code_base64 && (
                      <div className="bg-white rounded-xl p-4">
                        <img className="w-full aspect-square object-contain" src={`data:image/png;base64,${membershipPayment.pix.qr_code_base64}`} alt="QR Code Pix do plano" />
                      </div>
                    )}
                    <div>
                      <label className="label text-sm">Pix copia e cola</label>
                      <textarea className="input min-h-[96px] text-xs" readOnly value={membershipPayment.pix?.qr_code || ''} />
                    </div>
                    <button type="button" onClick={() => navigator.clipboard.writeText(membershipPayment.pix?.qr_code || '')} className="btn-secondary w-full">
                      Copiar Pix
                    </button>
                    <button type="button" onClick={checkMembershipPayment} disabled={loading} className="btn-primary w-full">
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Ja paguei
                    </button>
                  </>
                )}
              </div>
            ) : (
              <form onSubmit={subscribeToPlan} className="space-y-4">
                <div>
                  <label className="label mb-2">Seu nome</label>
                  <input className="input" required value={subscribeForm.nome} onChange={(e) => setSubscribeForm({ ...subscribeForm, nome: e.target.value })} />
                </div>
                <div>
                  <label className="label mb-2">WhatsApp</label>
                  <input className="input" required value={subscribeForm.telefone} onChange={(e) => setSubscribeForm({ ...subscribeForm, telefone: e.target.value })} />
                </div>
                <div>
                  <label className="label mb-2">Email para Pix</label>
                  <input className="input" type="email" placeholder="voce@email.com" value={subscribeForm.email} onChange={(e) => setSubscribeForm({ ...subscribeForm, email: e.target.value })} />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Gerar Pix do plano
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
